import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { DatePicker, Message, Select, Tooltip } from "@arco-design/web-react";
import { IconArrowLeft, IconLink } from "@arco-design/web-react/icon";
import dayjs from "dayjs";
import axios from "axios";

import { endpoints } from "../api/endpoints";
import { HomeMeetingCard } from "../components/HomeMeetingCard/HomeMeetingCard";
import { SimilarMentorCard } from "../components/SimilarMentorCard/SimilarMentorCard";
import { LinkedInInIcon } from "../components/LinkedInInIcon";
import { Meeting } from "../models/Meeting";
import { User } from "../models/Person";
import { routes } from "../routes/routes";
import { RootState } from "../store/store";
import { UserAvatar } from "../components/UserAvatar/UserAvatar";
import { fixUrl } from "../utils/fixUrl";
import { getCurrentDate } from "../utils/functions";
import { useTranslations } from "../utils/hooks/useTranslations";
import { buildMeetingGridSlots } from "../utils/meetingGrid";
import {
  getIntakeMatchForMentor,
  getIntakeReturnPath,
} from "./Intake/services/intakeMatchStorage";
import { useGuestActionGuard } from "../hooks/useGuestActionGuard";
import { trackEvent, viewedUserRole } from "../analytics/track";
import { QR_BOOKING_SOURCE, QR_PROFILE_SOURCE, isQrBookingSource, isQrProfileSource } from "../constants/qrProfile";

import "./MentorProfilePreview.css";

const Option = Select.Option;

const show24h = !Intl.DateTimeFormat("en-US", {
  hour: "numeric",
}).resolvedOptions().hour12;

function formatHourLabel(hour: number): string {
  if (show24h) {
    return `${hour}:00`;
  }
  const h = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${h}:00 ${hour >= 12 ? "PM" : "AM"}`;
}

function asStringArray(value: string | string[] | null | undefined): string[] {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.length > 0) return [value];
  return [];
}

function translateList(
  translateSelected: ReturnType<typeof useTranslations>["translateSelected"],
  listType: Parameters<ReturnType<typeof useTranslations>["translateSelected"]>[0],
  items: string[] | null | undefined
): string[] {
  const result = translateSelected(listType, asStringArray(items));
  return Array.isArray(result) ? result : asStringArray(result);
}

function getLanguageTags(language: string | undefined, t: (key: string) => string): string[] {
  if (!language) return [];
  if (language === "both") {
    return [t("mentor_profile.language_english"), t("mentor_profile.language_spanish")];
  }
  if (language === "es") return [t("mentor_profile.language_spanish")];
  return [t("mentor_profile.language_english")];
}

export const MentorProfilePreview = () => {
  const { t } = useTranslation();
  const { translateSelected } = useTranslations();
  const { guardAction, handleGuestApiError } = useGuestActionGuard();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isCalendlyLinkRequired = useSelector(
    (state: RootState) => state.config.isCalendlyLinkRequired
  );
  const isSurveyForm = useSelector((state: RootState) => state.config.isSurveyForm);

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const profileSource = searchParams.get("source");
  const location = useLocation();
  const intakeMatch = id ? getIntakeMatchForMentor(id) : null;
  const fromIntake =
    Boolean((location.state as { fromIntake?: boolean } | null)?.fromIntake) ||
    intakeMatch !== null;
  const intakeReturnPath = getIntakeReturnPath() ?? routes.INTAKE;
  const currentUserID = useSelector((state: RootState) => state.user.user?.id);
  const currentUserEmail = useSelector((state: RootState) => state.user.user?.email);

  const navigate = useNavigate();
  const [isBooking, setIsBooking] = useState(false);
  const [availableDayHours, setAvailableDayHours] = useState(
    {} as Record<string, { date: string; time: number }[]>
  );
  const [availableDays, setAvailableDays] = useState([] as string[]);
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedHour, setSelectedHour] = useState<number | undefined>(undefined);
  const [hours, setHours] = useState([] as number[]);
  const [data, setData] = useState<User>({} as User);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [similarMentors, setSimilarMentors] = useState<User[]>([]);

  const loadMeetings = () => {
    axios
      .get(`${endpoints.meetingsWithUser}${id}?timezone=${encodeURIComponent(timezone)}`)
      .then((response) => {
        setMeetings(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    loadMeetings();
  }, [id, isBooking]);

  const loadBooking = () => {
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    axios
      .get(`${endpoints.getAvailableHours}${id}?timezone=${localTimezone}`)
      .then((response) => {
        if (response.data.slots) {
          setAvailableDayHours(response.data.slots);
          const days = Object.keys(response.data.slots);
          setAvailableDays(days);
          if (!selectedDay && days.length > 0) {
            const initialDay = days.includes(getCurrentDate()) ? getCurrentDate() : days.sort()[0];
            selectDay(initialDay, response.data.slots);
          }
        }
      });
  };

  useEffect(() => {
    if (!data.isMentor) {
      return;
    }
    loadBooking();
  }, [id, isBooking, data.isMentor]);

  const selectDay = (
    day: string,
    slots: Record<string, { date: string; time: number }[]> = availableDayHours
  ) => {
    const dayHours = slots[day];
    if (dayHours) {
      setHours(dayHours.map((slot) => slot.time));
    } else {
      setHours([]);
    }
    setSelectedDay(day);
    setSelectedHour(undefined);
  };

  const bookMeeting = () => {
    guardAction(() => {
      if (!selectedDay) {
        Message.error(t("notification_messages.error_no_selected_day"));
        return;
      }
      if (selectedHour === undefined) {
        Message.error(t("mentor_profile.select_time_error"));
        return;
      }
      const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      axios
        .post(`${endpoints.bookMeeting}`, {
          mentor_id: data.id,
          date: selectedDay,
          hour: selectedHour,
          timezone: localTimezone,
        })
        .then(() => {
          Message.success({
            content: t("notification_messages.meeting_booked_success"),
            closable: true,
            duration: 3000,
          });
          trackEvent("book_meeting", {
            method: "in_app",
            signup_source: isQrProfileSource(profileSource) || isQrBookingSource(profileSource)
              ? (profileSource ?? QR_BOOKING_SOURCE)
              : "organic",
          });
          setIsBooking(false);
          setSelectedHour(undefined);
        })
        .catch((error) => {
          if (handleGuestApiError(error)) {
            return;
          }
          console.error("Error booking meeting:", error);
          Message.error(t("notification_messages.meeting_booked_error"));
        })
        .finally(() => {
          loadBooking();
          loadMeetings();
        });
    });
  };

  const handleStartConversation = () => {
    guardAction(() => {
      navigate(routes.MESSAGES, {
        state: {
          userId: id,
          name: `${data.name} ${data.last_name}`,
          isMentor: data.isMentor,
        },
      });
    });
  };

  useEffect(() => {
    axios
      .get(`${endpoints.getPublicProfile}${id}`)
      .then((response) => {
        setData(response.data);
        if (response.data?.id) {
          trackEvent("profile_view", {
            viewed_user_role: viewedUserRole(response.data),
            signup_source: isQrProfileSource(profileSource) || isQrBookingSource(profileSource)
              ? (profileSource ?? QR_PROFILE_SOURCE)
              : undefined,
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching profile data:", error);
      });
  }, [id, profileSource]);

  useEffect(() => {
    if (!data.isMentor) {
      setSimilarMentors([]);
      return;
    }
    axios
      .get(endpoints.getSearchMentors)
      .then((response) => {
        const mentors: User[] = response.data?.mentors ?? [];
        setSimilarMentors(
          mentors.filter((m) => m.id.toString() !== id).slice(0, 4)
        );
      })
      .catch((error) => {
        console.error("Error fetching similar mentors:", error);
      });
  }, [id, data.isMentor]);

  const industryTags = useMemo(
    () =>
      translateList(
        translateSelected,
        isSurveyForm ? "industries_nyc" : "industries",
        data.industries
      ),
    [data.industries, isSurveyForm, translateSelected]
  );

  const mentorTopics = useMemo(
    () => translateList(translateSelected, "categories", data.categories),
    [data.categories, translateSelected]
  );

  const languageTags = useMemo(
    () => getLanguageTags(data.language, t),
    [data.language, t]
  );

  const mentorDisplayName = `${data.name ?? ""} ${data.last_name ?? ""}`.trim();
  const mentorFirstName = data.name ?? mentorDisplayName;
  const isOwnProfile = !!currentUserID && data.id === currentUserID;
  const isMentor = !!data.isMentor;
  const showMentorActions = isMentor && !isOwnProfile;
  const useMultiColumnBio = (data.fullBio?.length ?? 0) > 320;

  const isInnerBookingAvailable =
    isMentor &&
    !isCalendlyLinkRequired &&
    ((availableDays && availableDays.length > 0) || isBooking);
  const isCalendlyBookingAvailable = isMentor && isCalendlyLinkRequired && !!data.calendlyLink;
  const isBookingButtonDisabled = isCalendlyLinkRequired
    ? !isCalendlyBookingAvailable
    : !isInnerBookingAvailable;

  const handleBookSession = () => {
    guardAction(() => {
      if (isCalendlyLinkRequired) {
        window.open(data.calendlyLink, "_blank");
        trackEvent("book_meeting", { method: "calendly" });
        return;
      }
      setIsBooking((prev) => {
        const next = !prev;
        if (next) {
          trackEvent("availability_view");
        }
        return next;
      });
    });
  };

  const bookButtonLabel = isCalendlyLinkRequired
    ? t("common.book_via_calendly")
    : !availableDays || availableDays.length === 0
      ? t("mentor_profile.no_available_slots")
      : t("mentor_profile.book_time_button");

  if (!data?.id) {
    return <div className="mentor-profile__loading">{t("common.loading_data")}</div>;
  }

  return (
    <div className="mentor-profile-page">
      {fromIntake ? (
        <button
          type="button"
          className="mentor-profile__back"
          onClick={() => navigate(intakeReturnPath)}
        >
          <IconArrowLeft />
          {t("mentor_profile.back_to_matches")}
        </button>
      ) : (
        !isOwnProfile && (
          <button
            type="button"
            className="mentor-profile__back"
            onClick={() => navigate(routes.SEARCH)}
          >
            <IconArrowLeft />
            {t("mentor_profile.back_to_results")}
          </button>
        )
      )}

      <header className="mentor-profile__header">
        <div className="mentor-profile__photo">
          <div className="mentor-profile__photo-inner">
            <UserAvatar
              avatarUrl={data.avatar_url}
              userId={data.id}
              firstName={data.name}
              lastName={data.last_name}
              shape="square"
              alt={mentorDisplayName}
            />
          </div>
        </div>

        <div className="mentor-profile__header-content">
          <h1 className="mentor-profile__name">{mentorDisplayName}</h1>

          {!!data.shortDescription && (
            <p className="mentor-profile__title">{data.shortDescription}</p>
          )}

          {(!!data.linkedIn || !!data.website) && (
            <div className="mentor-profile__social-row">
              {!!data.linkedIn && (
                <a
                  className="mentor-profile__social-btn"
                  href={fixUrl(data.linkedIn)}
                  target="_blank"
                  rel="noreferrer noopener nofollow"
                  aria-label={t("mentor_profile.linkedin")}
                >
                  <LinkedInInIcon />
                </a>
              )}
              {!!data.website && (
                <a
                  className="mentor-profile__social-btn"
                  href={fixUrl(data.website)}
                  target="_blank"
                  rel="noreferrer noopener nofollow"
                  aria-label={t("mentor_profile.website")}
                >
                  <IconLink />
                </a>
              )}
            </div>
          )}

          {industryTags.length > 0 && (
            <div className="mentor-profile__label-block">
              <p className="mentor-profile__label">{t("mentor_profile.my_industries")}</p>
              <div className="mentor-profile__tags">
                {industryTags.map((tag) => (
                  <span key={tag} className="mentor-profile__tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {isMentor && mentorTopics.length > 0 && (
            <div className="mentor-profile__label-block">
              <p className="mentor-profile__label">{t("mentor_profile.can_mentor_on")}</p>
              <ul className="mentor-profile__mentor-list">
                {mentorTopics.map((topic) => (
                  <li key={topic}>{topic}</li>
                ))}
              </ul>
            </div>
          )}

          {isMentor && languageTags.length > 0 && (
            <div className="mentor-profile__label-block">
              <p className="mentor-profile__label">{t("mentor_profile.languages_label")}</p>
              <div className="mentor-profile__tags">
                {languageTags.map((tag) => (
                  <span key={tag} className="mentor-profile__tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {showMentorActions && (
            <div className="mentor-profile__actions">
              <Tooltip
                className="profile-booking-link-tooltip"
                content={
                  data.calendlyLink
                    ? t("common.calendly_email_warning", { email: currentUserEmail })
                    : t("common.no_calendly_link_message")
                }
                disabled={!isCalendlyLinkRequired}
              >
                <button
                  type="button"
                  className="mentor-profile__btn mentor-profile__btn--primary"
                  onClick={handleBookSession}
                  disabled={isBookingButtonDisabled}
                >
                  {bookButtonLabel}
                </button>
              </Tooltip>

              <button
                type="button"
                className="mentor-profile__btn mentor-profile__btn--outline"
                onClick={handleStartConversation}
              >
                {t("mentor_profile.send_message_button")}
              </button>
            </div>
          )}
        </div>
      </header>

      {isBooking && isMentor && !isCalendlyLinkRequired && (
        <section className="mentor-profile__book-panel" aria-label={t("mentor_profile.book_time_heading")}>
          <div className="mentor-profile__book-panel-inner">
          <button
            type="button"
            className="mentor-profile__book-panel-close"
            onClick={() => setIsBooking(false)}
            aria-label={t("common.close_button")}
          >
            ×
          </button>
          <h2 className="mentor-profile__book-panel-title">{t("mentor_profile.book_time_heading")}</h2>

          {availableDays.length > 0 ? (
            <div className="mentor-profile__book-panel-grid">
              <p className="mentor-profile__book-intro">{t("mentor_profile.book_time_intro")}</p>

              <div className="mentor-profile__book-form">
                <div>
                  <span className="mentor-profile__field-label" id="mentor-book-date-label">
                    {t("mentor_profile.choose_date_label")}
                  </span>
                  <DatePicker
                    style={{ width: "100%" }}
                    disabledDate={(current) =>
                      !availableDays.includes(dayjs(current).format("YYYY-MM-DD"))
                    }
                    placeholder={t("mentor_profile.select_date_placeholder")}
                    onChange={(date) => {
                      if (date) {
                        selectDay(dayjs(date).format("YYYY-MM-DD"));
                      }
                    }}
                  />
                </div>

                {!!selectedDay && hours.length > 0 && (
                  <div>
                    <span className="mentor-profile__field-label" id="mentor-book-time-label">
                      {t("mentor_profile.choose_time_label")}
                    </span>
                    <Select
                      placeholder={t("mentor_profile.choose_time_placeholder")}
                      value={selectedHour}
                      onChange={(value) => setSelectedHour(value as number)}
                      style={{ width: "100%" }}
                      aria-labelledby="mentor-book-time-label"
                    >
                      {hours.map((hour) => (
                        <Option key={hour} value={hour}>
                          {formatHourLabel(hour)}
                        </Option>
                      ))}
                    </Select>
                  </div>
                )}

                <div className="mentor-profile__book-footer">
                  <button
                    type="button"
                    className="mentor-profile__btn mentor-profile__btn--primary"
                    onClick={bookMeeting}
                    disabled={selectedHour === undefined}
                  >
                    {t("mentor_profile.confirm_booking_button")}
                  </button>
                  <p className="mentor-profile__book-note">
                    {t("mentor_profile.timezone_message", { timezone })}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="mentor-profile__book-intro">{t("mentor_profile.no_available_hours")}</p>
          )}
          </div>
        </section>
      )}

      {fromIntake && intakeMatch?.whyYouMatch && (
        <section className="mentor-profile__learn-more">
          <h2 className="mentor-profile__learn-more-title">
            {t("mentor_profile.why_you_match")}
          </h2>
          <p className="mentor-profile__bio-columns">{intakeMatch.whyYouMatch}</p>
        </section>
      )}

      {!!data.fullBio && (
        <section className="mentor-profile__learn-more">
          <h2 className="mentor-profile__learn-more-title">
            {t("mentor_profile.learn_more_heading", { name: mentorFirstName.toUpperCase() })}
          </h2>
          <p
            className={`mentor-profile__bio-columns${
              useMultiColumnBio ? " mentor-profile__bio-columns--multi" : ""
            }`}
          >
            {data.fullBio}
          </p>
        </section>
      )}

      {isMentor && similarMentors.length > 0 && (
        <section className="mentor-profile__similar">
          <h2 className="mentor-profile__similar-title">
            {t("mentor_profile.similar_mentors_heading", { name: mentorFirstName })}
          </h2>
          <div className="mentor-profile__similar-grid">
            {similarMentors.map((mentor) => (
              <SimilarMentorCard key={mentor.id} mentor={mentor} />
            ))}
          </div>
        </section>
      )}

      {meetings.length > 0 && (
        <section className="mentor-profile__meetings">
          <h2 className="mentor-profile__meetings-title">
            {t("mentor_profile.upcoming_meetings")}
            {data.isMentor ? t("mentor_profile.mentor") : t("mentor_profile.person")}
          </h2>
          <div
            className="mentor-profile__meetings-scroll"
            tabIndex={0}
            aria-label={t("mentor_profile.upcoming_meetings")}
          >
            <div className="mentor-profile__meetings-grid">
              {buildMeetingGridSlots(meetings).map((meeting, index) =>
                meeting ? (
                  <div key={meeting.id} className="home-meeting-card-wrap">
                    <HomeMeetingCard
                      meeting={meeting}
                      onCancelMeeting={loadMeetings}
                    />
                  </div>
                ) : (
                  <div
                    key={`meeting-slot-empty-${index}`}
                    className="home-meeting-card-wrap home-meeting-card-wrap--empty"
                    aria-hidden="true"
                  />
                )
              )}
            </div>
          </div>
          <p className="mentor-profile__book-note">
            {t("mentor_profile.timezone_message", { timezone })}
          </p>
        </section>
      )}
    </div>
  );
};
